// What a failed run means to show, decided once so `program.js` only renders it. Pure
// -- no DOM -- so `viewer/test/failure.mjs` can exercise every case without a page.
//
// `answer` is a job's result dict (`status`, `kind`, `message`, `error`, `stderr_tail`,
// `stdout_tail`) or, for a submission that never reached the queue, an HTTP error body
// (`{error}`) with `http` set to the response status the caller saw (429/503/400/413/a
// 5xx, or 0 for a request that never got an answer at all). `job` is the job state
// (`elapsed_s`); the job carries no topology, so pass the chosen slice as `topology`
// (a third argument, or `job.topology`).

const TRACEBACK_MARK = "Traceback (most recent call last):";
// The exception line of a traceback -- kept in sync by hand with `jobs._PYTHON_ERROR`,
// since this is the client-side fallback for a server too old to send `error` itself.
const EXCEPTION_LINE = /^(?:\w+\.)*[A-Z]\w*(?:Error|Exception|Exit|Interrupt|Warning|StopIteration)\b/;
const FRAME = /File "(?:\/launch\/)?program\.py", line (\d+)/g;

const OURS_HINT = "This is on our side, not your program: it has been logged and we " +
  "look at every one. Try another slice, or a smaller change.";
// The wall-clock budget is in the headline the server sends ("the program ran out of
// time (N s)"), so the hint gives the advice without a number that would go stale.
const TIMEOUT_HINT = "That budget is a wall clock on a shared box, compile included. " +
  "Make it smaller: fewer steps, a smaller model, a shorter loop.";
// The memory budget, like the wall clock, is in the headline ("ran out of memory (N GiB)").
const OOM_HINT = "That budget holds the program and the simulated chip's memory together. " +
  "Make it smaller: smaller arrays, a smaller batch, fewer copies kept alive.";
const NO_TRACE_HINT = "There is nothing to show without a trace: put some of the work on " +
  "the TPU with jax (a jitted function, or any jax.numpy operation).";

// Belt and braces: the server already cuts `stderr_tail` to the last traceback
// (`jobs._shown_tail`), but trim again in case it didn't.
function trimToTraceback(text) {
  const at = (text || "").lastIndexOf(TRACEBACK_MARK);
  return at === -1 ? (text || "") : text.slice(at);
}

function scanForException(text) {
  const lines = (text || "").split("\n");
  for (let i = lines.length - 1; i >= 0; i--) {
    if (EXCEPTION_LINE.test(lines[i])) return lines[i].trim();
  }
  return "";
}

// Unique program line numbers, in the order their frames appear.
function programLines(text) {
  const out = [];
  for (const m of (text || "").matchAll(FRAME)) {
    const n = Number(m[1]);
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

function detail(what, slice, elapsed) {
  return elapsed == null ? "" : `${what} ${slice ? `on ${slice} ` : ""}after ${elapsed} s`;
}

export function describe(answer, job, topology) {
  answer = answer || {};
  job = job || {};
  const slice = topology || job.topology || "";
  const elapsed = job.elapsed_s;
  const http = answer.http;

  if (http === 0) {
    // A submit Cloudflare's rate limit refused looks exactly like this: its 429 carries no
    // CORS header, so the page never sees the status, only a failed fetch.
    const hint = answer.submit
      ? "Submissions from one address are rate-limited: wait 10 seconds and try again. " +
        "If it keeps failing, check the connection."
      : "Check the connection and try again.";
    return { tone: "network", headline: answer.error || "the server did not answer",
      detail: "", hint, traceback: "", lines: [], stdout: "" };
  }
  if (http === 429) {
    return { tone: "queue", headline: "the queue is full", detail: answer.error || "",
      hint: "Try again in a minute.", traceback: "", lines: [], stdout: "" };
  }
  if (http === 503) {
    return { tone: "queue", headline: "submissions are closed", detail: answer.error || "",
      hint: "", traceback: "", lines: [], stdout: "" };
  }
  if (http === 400 || http === 413) {
    return { tone: "request", headline: answer.error || answer.message || `the server said ${http}`,
      detail: "", hint: "", traceback: "", lines: [], stdout: "" };
  }
  if (typeof http === "number" && http >= 500) {
    return { tone: "ours", headline: answer.error || answer.message || "the run failed",
      detail: "", hint: OURS_HINT, traceback: "", lines: [], stdout: "" };
  }

  if (answer.kind === "simulator") {
    return { tone: "ours", headline: "the simulator could not run this program",
      detail: detail("failed", slice, elapsed), hint: OURS_HINT,
      traceback: "", lines: [], stdout: answer.stdout_tail || "" };
  }

  // Their own code: an error, a loop that ran out of time or memory, or a program that
  // never used the TPU at all.
  const traceback = trimToTraceback(answer.stderr_tail);
  const lines = programLines(traceback);
  const stdout = answer.stdout_tail || "";
  if (answer.status === "oom") {
    // The kill leaves no exception; what stderr ends on is not why, so it is not shown.
    return { tone: "program", headline: answer.message || "the program ran out of memory",
      detail: detail("killed", slice, elapsed), hint: OOM_HINT, traceback: "", lines: [], stdout };
  }
  if (answer.status === "no_trace") {
    // It ran and exited cleanly; its output is the result.
    return { tone: "program",
      headline: answer.message || "this program finished without running anything on the TPU (no trace)",
      detail: detail("finished", slice, elapsed), hint: NO_TRACE_HINT, traceback: "", lines: [], stdout,
      stdoutOpen: true };
  }
  if (answer.status === "timeout") {
    // The reason is the timeout, nothing else. The stderr ends wherever the kill landed
    // -- often on a simulator "scheduler stall" from a core cut off mid-wait -- which is
    // a symptom of the kill, not why the run failed, so it is not shown. The program's
    // own stdout still is.
    return { tone: "program", headline: answer.message || "the program ran out of time",
      detail: detail("timed out", slice, elapsed), hint: TIMEOUT_HINT, traceback: "", lines: [], stdout };
  }
  const headline = answer.error || scanForException(traceback) || answer.message || "the run failed";
  return { tone: "program", headline, detail: detail("failed", slice, elapsed), hint: "",
    traceback, lines, stdout };
}
